import { Link, useSearchParams } from 'react-router-dom'
import { ErpTable, EmptyRow, Panel, SectionHeader, Td, Th } from '@/components/erp/primitives'
import { FilterBar } from '@/shared/ui/filters'
import { ErrorStrip, Forbidden, LoadingBlock } from '@/shared/ui/states'
import { useCan } from '@/shared/auth/session'
import { useLoad } from '@/features/administration/hooks'
import { fmtDate } from '@/shared/lib/format'
import { listPolls } from './api'
import { PollDrawer } from './PollDrawer'
import { CODE_LIST_UNAVAILABLE, useCodes } from './useVocabulary'
import {
  Pill, categoryLabel, count, methodologyLabel, pollStatusLabel, pollStatusTone,
} from './vocab'

/**
 * Every poll, with its status and its scope.
 *
 * ## The filter is in the URL
 *
 * So a link to "live trackers in Kisumu" is a link somebody can send. The
 * selected poll is in the URL too — `?poll=<id>` opens the drawer — which is what
 * makes the Overview screen able to link straight into one.
 */
export function PollsListPage() {
  const canRead = useCan('survey:read')
  const canCreate = useCan('survey:create')
  const [params, setParams] = useSearchParams()

  const status = params.get('status') ?? ''
  const search = params.get('q') ?? ''
  const selected = params.get('poll')

  const set = (key: string, value: string) => {
    const next = new URLSearchParams(params)
    if (value) next.set(key, value)
    else next.delete(key)
    setParams(next, { replace: true })
  }

  const statuses = useCodes('status')

  const polls = useLoad(
    () => listPolls({ statusCode: status || undefined, search: search || undefined }, 0, 100),
    [status, search])

  if (!canRead) return <Forbidden what="polls" />

  const rows = polls.data?.content ?? []

  return (
    <div className="space-y-4">
      <SectionHeader
        title="Polls"
        description="A poll is an instrument, a scope and a sample design. Its status is a position in the state machine."
        right={canCreate ? (
          // The step-wise configurator, not a modal. Creating a poll used to be
          // four fields in a dialog, after which the eight remaining decisions
          // were scattered across five screens with nothing saying they existed
          // or in what order they mattered.
          <Link to="/polling/configure?step=scope" className="erp-btn-primary h-9 text-erp-sm">
            New poll
          </Link>
        ) : undefined}
      />

      <FilterBar>
        <label className="flex items-center gap-1.5 text-erp-xs">
          Status
          <select className="erp-input h-9" value={status}
            disabled={statuses.loading || statuses.unavailable}
            onChange={(e) => set('status', e.target.value)}>
            <option value="">Any</option>
            {statuses.codes.map((code) => (
              <option key={code} value={code}>{pollStatusLabel(code)}</option>
            ))}
          </select>
        </label>
        {statuses.unavailable && (
          // Next to the control it disables, in visible text: a `title` on a
          // disabled select is unreachable by mouse and by keyboard both.
          <span className="text-erp-xs text-red-700 max-w-xs">{CODE_LIST_UNAVAILABLE}</span>
        )}
        <label className="flex items-center gap-1.5 text-erp-xs">
          Search
          <input className="erp-input h-9" placeholder="Ref or title" value={search}
            onChange={(e) => set('q', e.target.value)} />
        </label>
        {(status || search) && (
          <button type="button" className="erp-btn-ghost h-9 text-erp-sm text-chocolate"
            onClick={() => setParams(new URLSearchParams(), { replace: true })}>
            Clear
          </button>
        )}
      </FilterBar>

      <ErrorStrip message={polls.error ?? statuses.error} />

      {polls.loading && rows.length === 0 ? <LoadingBlock label="Loading polls…" /> : (
        <Panel title={`${rows.length} poll${rows.length === 1 ? '' : 's'}`}>
          <ErpTable head={(
            <>
              <Th>Ref</Th>
              <Th>Title</Th>
              <Th>Status</Th>
              <Th>Category</Th>
              <Th>Method</Th>
              <Th>Scope</Th>
              <Th className="text-right">Target</Th>
              <Th className="text-right">Version</Th>
              <Th>Closes</Th>
            </>
          )}>
            {rows.length === 0 ? (
              <EmptyRow colSpan={9} message={
                status || search
                  ? 'No poll matches that filter. Clear it to see everything.'
                  : 'No polls yet. Create one — it starts as a draft with an empty instrument.'
              } />
            ) : rows.map((poll) => (
              <tr
                key={poll.id}
                className="hover:bg-muted/40 cursor-pointer"
                onClick={() => set('poll', poll.id)}
              >
                <Td className="font-mono text-erp-xs text-chocolate">{poll.ref}</Td>
                <Td>{poll.title}</Td>
                <Td>
                  <Pill tone={pollStatusTone(poll.statusCode)}>
                    {pollStatusLabel(poll.statusCode)}
                  </Pill>
                </Td>
                <Td className="text-muted-foreground">{categoryLabel(poll.categoryCode)}</Td>
                <Td className="text-muted-foreground">{methodologyLabel(poll.methodologyCode)}</Td>
                <Td className="text-muted-foreground">{poll.scopeUnitName ?? '—'}</Td>
                <Td className="text-right tabular-nums">{count(poll.targetSample)}</Td>
                <Td className="text-right tabular-nums text-muted-foreground">
                  {poll.currentVersion ? `v${poll.currentVersion.versionNo}` : '—'}
                </Td>
                <Td className="text-muted-foreground">
                  {poll.closesAt ? fmtDate(poll.closesAt) : '—'}
                </Td>
              </tr>
            ))}
          </ErpTable>
        </Panel>
      )}

      {/*
        * KEYED BY THE ROW, so React remounts it instead of reusing it.
        *
        * A drawer that stays mounted across rows keeps every piece of local
        * state from the last one: a half-open form, a typed value, and — worst —
        * the previous row's error. Opening party B after a refusal on party A
        * showed A's refusal under B's name, and the "Join" button toggled a form
        * that was already open from A, so it appeared to do nothing. One line
        * here is cheaper and more reliable than an effect that resets six
        * useStates and has to be extended every time a seventh is added.
        */}
      <PollDrawer key={selected ?? 'none'}
        pollId={selected}
        onClose={() => set('poll', '')}
        onChanged={() => polls.reload()}
      />

    </div>
  )
}
